import { useEffect, useState } from 'react'
import { useScript } from 'usehooks-ts'
import RichText from '@/components/ui/rich-text.tsx'
import type { TypeComponentHubSpotForm } from '@/types/ctf'

import { usePage } from '@inertiajs/react'
import { localizedPath, useLocale, useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'
import ShieldLoader from '@/components/ui/shield-loader'
import DOMPurify from 'isomorphic-dompurify'

type HubspotFormFields = TypeComponentHubSpotForm<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
export type HubspotFormProps = Omit<HubspotFormFields, 'formSize'> & {
  formSize?: HubspotFormFields['formSize'] | 'Xsmall'
}

export default function HubspotForm({
  formId,
  headline,
  submitText,
  afterSubmitText,
  afterSubmitLink,
  translations,
  subtext,
  plausibleEventName,
  customErrorMessage,
  formSize,
  postFormText,
  shieldLoader,
}: HubspotFormProps) {
  const { url } = usePage()
  const { currentLocale } = useLocale(url)

  const [loadingError, setLoadingError] = useState(false)
  const [targetId, setTargetId] = useState<string | null>(null)
  const [formLoaded, setFormLoaded] = useState(false)
  const scriptStatus = useScript(`//js.hsforms.net/forms/embed/v2.js`, {
    removeOnUnmount: false,
  })

  const checkMITFormPage = () => url.includes('/go/start')
  const { t } = useTranslations()

  useEffect(() => {
    // set hs id to the form id and append a random number to avoid conflicts
    if (formId) {
      const randomSuffix = Math.floor(Math.random() * 1000)
      setTargetId(`hsf-${formId}-${randomSuffix}`)
    } else {
      setTargetId(null)
    }
  }, [])

  useEffect(() => {
    if (scriptStatus === 'ready' && targetId) {
      setLoadingError(false)
      window.hbspt.forms.create({
        region: 'na1',
        portalId: '22371289',
        formId: `${formId}`,
        pageId: formId,
        target: `#${targetId}`,
        locale: currentLocale,
        cssClass: plausibleEventName,
        ...(afterSubmitText && { inlineMessage: afterSubmitText }),
        ...(afterSubmitLink?.fields.url &&
          !afterSubmitText && { redirectUrl: localizedPath(afterSubmitLink.fields.url, currentLocale) }),
        translations: {
          [currentLocale]: {
            submitText,
            fieldLabels:
              translations
                ?.filter((t) => !t?.fields?.key?.startsWith('#'))
                .reduce(
                  (acc, curr) => {
                    if (curr?.fields?.key && curr?.fields?.phrase) {
                      acc[curr.fields.key] = curr?.fields?.phrase
                    }
                    return acc
                  },
                  {} as Record<string, string>
                ) || {},
          },
        },
        onFormReady: () => {
          translations
            ?.filter((t) => t?.fields.key.startsWith('#'))
            .forEach((t) => {
              const el = t?.fields.key ? document.getElementById(t?.fields.key.substring(1)) : null
              if (el && t?.fields.phrase) el.innerText = DOMPurify.sanitize(t.fields.phrase)
            })
          // Add tooltip to server select

          const serverSelectDescription = document.querySelector('.hs_trial_initiation_server_selected .hs-field-desc')

          // find the label inside serverSelectDescription and insert the serverSelectDescription
          const serverSelectDescriptionParent = document.querySelector(
            '.hs_trial_initiation_server_selected .hs-field-desc'
          )?.parentElement

          // Find the label element inside serverSelectDescriptionParent
          const labelElement = serverSelectDescriptionParent?.querySelector('label')

          // Only add tooltip if it hasn't been added already (prevent double execution)
          if (serverSelectDescription && !serverSelectDescription.querySelector('.bw-mit-info-icon')) {
            serverSelectDescription.classList.add('active')
            serverSelectDescription.innerHTML = `<span class="bw-hs-toolip-content">${DOMPurify.sanitize(serverSelectDescription.innerHTML)}</span>`
            serverSelectDescription.insertAdjacentHTML('afterbegin', '<span class="bw-mit-info-icon"></span>')

            if (labelElement) {
              labelElement.insertAdjacentHTML('beforeend', serverSelectDescription.outerHTML)
            }
            // Remove the original serverSelectDescription element
            serverSelectDescription.remove()
          }

          setFormLoaded(true)
        },
      })
    }
    if (scriptStatus === 'error') {
      setLoadingError(true)
    }
  }, [targetId, scriptStatus])

  return (
    <div
      className={cx(
        'hubspot-form relative',
        checkMITFormPage() && 'min-h-[375px] lg:min-h-[560px]',
        formSize === 'Small' && 'min-h-[165px]',
        formSize === 'Medium' && 'min-h-[300px]',
        formSize === 'Large' && 'min-h-[600px]'
      )}
      data-targetid={targetId}
      data-formid={formId}
    >
      <span className="hubspot-form-copy">
        {headline && <h3 className="hubspot-form-headline">{headline}</h3>}
        {subtext && <RichText body={subtext} />}
      </span>

      {loadingError && (
        <div className="border border-accent-melon bg-secondary-100 px-4 py-2 rounded-md [&_p]:text-base [&_p]:mb-0">
          {customErrorMessage ? (
            <RichText body={customErrorMessage} />
          ) : (
            <>
              {t('Your browser has blocked this form from loading')}.{' '}
              <a href="https://vault.bitwarden.com/#/signup">{t('Click here')}</a>{' '}
              {t('to set up your free Bitwarden account, or')}{' '}
              <a href={localizedPath('/contact/', currentLocale)}>{t('contact support')}</a> {t('for assistance')}.
            </>
          )}
        </div>
      )}
      <div className="hubspot-form-container relative" id={targetId}>
        {!loadingError && (
          <ShieldLoader
            containerHeight={checkMITFormPage() ? 350 : undefined}
            shieldSize={checkMITFormPage() ? 'Medium' : (shieldLoader ?? formSize)}
          />
        )}
      </div>
      {postFormText && formLoaded && (
        <div className="hubspot-form-post-tex [&_p]:text-sm [&_p]:text-pretty pt-8">
          <RichText body={postFormText} />
        </div>
      )}
    </div>
  )
}
